import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// Initial State
const initialState = {
    userSessions: [],
    currentUserSession: null,
    isLoading: false,
    error: null,
};

/* ==============================
   ASYNC THUNKS
============================== */

// Fetch all user sessions
export const fetchUserSessions = createAsyncThunk(
    'userSessions/fetchUserSessions',
    async (params, { rejectWithValue }) => {
        try {
            const response = await api.get('/user-sessions', { params });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch sessions');
        }
    }
);

// Fetch single user session by ID
export const fetchUserSession = createAsyncThunk(
    'userSessions/fetchUserSession',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/user-sessions/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch session details');
        }
    }
);

// Create new user session
export const createUserSession = createAsyncThunk(
    'userSessions/createUserSession',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await api.post('/user-sessions', formData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to create session');
        }
    }
);

// Update user session
export const updateUserSession = createAsyncThunk(
    'userSessions/updateUserSession',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/user-sessions/${id}`, formData);
            return response.data.data ?? response.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to update session');
        }
    }
);

// Delete user session
export const deleteUserSession = createAsyncThunk(
    'userSessions/deleteUserSession',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/user-sessions/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete session');
        }
    }
);

/* ==============================
   SLICE
============================== */

const userSessionSlice = createSlice({
    name: 'userSessions',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentUserSession: (state, action) => {
            state.currentUserSession = action.payload;
        },
        clearCurrentUserSession: (state) => {
            state.currentUserSession = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchUserSessions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserSessions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.userSessions = action.payload;
            })
            .addCase(fetchUserSessions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchUserSession.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserSession.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentUserSession = action.payload;
            })
            .addCase(fetchUserSession.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createUserSession.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createUserSession.fulfilled, (state, action) => {
                state.isLoading = false;
                if (!action.payload) return;
                state.userSessions.unshift(action.payload);
                state.currentUserSession = action.payload;
            })
            .addCase(createUserSession.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateUserSession.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateUserSession.fulfilled, (state, action) => {
                state.isLoading = false;
                const updatedData = action.payload.data || action.payload;
                if (!updatedData) return;

                const index = state.userSessions.findIndex((f) => f.id === updatedData.id);
                if (index !== -1) {
                    state.userSessions[index] = updatedData;
                }
                if (state.currentUserSession?.id === updatedData.id) {
                    state.currentUserSession = updatedData;
                }
            })
            .addCase(updateUserSession.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteUserSession.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteUserSession.fulfilled, (state, action) => {
                state.isLoading = false;
                state.userSessions = state.userSessions.filter((f) => f.id !== action.payload);
                if (state.currentUserSession?.id === action.payload) {
                    state.currentUserSession = null;
                }
            })
            .addCase(deleteUserSession.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, setCurrentUserSession, clearCurrentUserSession } = userSessionSlice.actions;
export default userSessionSlice.reducer;
